import { CATEGORY_KEYS, SOURCE_KEYS, STATUS_KEYS } from './feedbackMeta';

/**
 * Feedback list state in the URL:
 * ?page=2&limit=25&search=shell&status=pending&category=all&source=ios&days=7&id=1001
 * or a custom range ?from=2026-10-01&to=2026-10-20 (then `days` is left out).
 * Values are validated and fall back to defaults, so a hand-edited URL can't 400.
 */

export const PAGE_SIZES = [10, 25, 50, 100];
export const MIN_SEARCH_LENGTH = 2;
export const MAX_SEARCH_LENGTH = 100;
const DAY_OPTIONS = ['7', '30', '90', 'all'];

export const DEFAULT_PARAMS = Object.freeze({
  page: 1,
  limit: 10,
  search: '',
  status: 'all',
  category: 'all',
  source: 'all',
  days: '30',
  from: '',
  to: '',
  id: null,
});

const intIn = (raw, min, max) => (raw !== null && /^\d+$/.test(raw) && +raw >= min && +raw <= max ? +raw : null);

/** Real calendar date in YYYY-MM-DD. */
export function isIsoDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value ?? '')) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

export const isValidRange = (from, to) => isIsoDate(from) && isIsoDate(to) && from <= to;

export function parseFeedbackParams(searchParams) {
  const invalid = [];
  const get = (key) => searchParams.get(key);
  const pick = (key, value) => {
    if (get(key) === null) return DEFAULT_PARAMS[key];
    if (value === null) {
      invalid.push(key);
      return DEFAULT_PARAMS[key];
    }
    return value;
  };
  const oneOf = (key, allowed) => (allowed.includes(get(key)) ? get(key) : null);

  const rawSearch = get('search');
  const search = (rawSearch ?? '').trim().slice(0, MAX_SEARCH_LENGTH);
  if (rawSearch !== null && search.length < MIN_SEARCH_LENGTH) invalid.push('search');

  let from = '';
  let to = '';
  if (get('from') !== null || get('to') !== null) {
    if (isValidRange(get('from'), get('to'))) {
      from = get('from');
      to = get('to');
    } else {
      invalid.push('from');
    }
  }

  return {
    params: {
      page: pick('page', intIn(get('page'), 1, Number.MAX_SAFE_INTEGER)),
      limit: pick('limit', intIn(get('limit'), 1, 100)),
      search: search.length >= MIN_SEARCH_LENGTH ? search : '',
      status: pick('status', oneOf('status', ['all', ...STATUS_KEYS])),
      category: pick('category', oneOf('category', ['all', ...CATEGORY_KEYS])),
      source: pick('source', oneOf('source', ['all', ...SOURCE_KEYS])),
      // A custom range wins; `days` is ignored alongside it
      days: from ? DEFAULT_PARAMS.days : pick('days', oneOf('days', DAY_OPTIONS)),
      from,
      to,
      id: pick('id', intIn(get('id'), 1, Number.MAX_SAFE_INTEGER)),
    },
    invalid,
  };
}

/** URL query string; defaults are left out, and `days` is dropped when from/to are set. */
export function toQueryString(params) {
  const qs = new URLSearchParams();
  const keys = ['page', 'limit', 'search', 'status', 'category', 'source'];
  for (const key of keys) {
    if (params[key] !== DEFAULT_PARAMS[key] && params[key] !== '') qs.set(key, String(params[key]));
  }
  if (params.from && params.to) {
    qs.set('from', params.from);
    qs.set('to', params.to);
  } else if (params.days !== DEFAULT_PARAMS.days) {
    qs.set('days', params.days);
  }
  if (params.id) qs.set('id', String(params.id));
  return qs.toString();
}

/** Filter changes go back to page 1; changing only the page or the open ticket keeps it. */
export function updateParams(current, changes) {
  const next = { ...current, ...changes };
  if (!Object.keys(changes).every((k) => k === 'page' || k === 'id')) next.page = 'page' in changes ? changes.page : 1;
  return next;
}

/** The query the API gets: `days` as a number (none for "all"), or from/to. */
export function toApiParams({ page, limit, search, status, category, source, days, from, to }) {
  if (from && to) return { page, limit, search, status, category, source, from, to };
  return { page, limit, search, status, category, source, days: days === 'all' ? null : Number(days) };
}
